import React, { useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import { Button, Alert, CircularProgress, Typography, Grid, Divider, TextField, Box } from '@mui/material';
import PropTypes from 'prop-types';

import { USERNAME_REGEX, EMAIL_REGEX } from '../../utils/constants';
import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import { EMAIL_EXCEPTION, INTERNAL_SERVER_ERROR, REQUIRED_FIELD, USERNAME_EXCEPTION } from '../../utils/exception';

export default function EditCredential({ userToBeUpdated, handleRefreshUserData, updaterId }) {
  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [disabled, setDisabled] = useState(false);
  const [firstDisabled, setFirstDisabled] = useState(true);

  const [username, setUsername] = useState(userToBeUpdated.username || null);
  const [usernameClicked, setUsernameClicked] = useState(false);
  const [formErrorOnUsername, setFormErrorOnUsername] = useState(false);
  const [helperTextOnUsername, setHelperTextOnUsername] = useState(null);

  const [email, setEmail] = useState(userToBeUpdated.email || null);
  const [emailClicked, setEmailClicked] = useState(false);
  const [formErrorOnEmail, setFormErrorOnEmail] = useState(false);
  const [helperTextOnEmail, setHelperTextOnEmail] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);

    if (!formErrorOnUsername && !formErrorOnEmail) {
      try {
        const response = await axiosInstance.patch(
          `${SERVER_API_URL}/user/updateUserCredential/${userToBeUpdated && userToBeUpdated.id}/${updaterId}`,
          {
            username,
            email,
          }
        );

        if (response.status === 200) {
          setFormError('የተጠቃሚ የፍቃድ መስክ በተሳካ ሁኔታ ዘምኗል።');
          setFormErrorSeverity('success');
        }
      } catch (e) {
        if (e.response.status === 406 || e.response.status === 400 || e.response.status === 404) {
          setFormError(e.response.data.message);
        } else if (e.response.status === 500) {
          setFormError(INTERNAL_SERVER_ERROR);
        }
      }
    } else {
      setFormError('እባክዎ የሚፈለጉትን መስኮች በትክክል ይሙሉ!');
    }
    setDisabled(false);
    setFirstDisabled(true);
  };

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'username':
        if (value.length === 0) {
          setUsernameClicked(false);
          setFormErrorOnUsername(usernameClicked && value.length === 0);
          setHelperTextOnUsername(REQUIRED_FIELD);
        } else if (!USERNAME_REGEX.test(value)) {
          setFormErrorOnUsername(true);
          setHelperTextOnUsername(USERNAME_EXCEPTION);
        } else {
          setHelperTextOnUsername(null);
          setFormErrorOnUsername(false);
        }
        break;
      case 'email':
        if (value.length === 0) {
          setEmailClicked(false);
          setFormErrorOnEmail(emailClicked && value.length === 0);
          setHelperTextOnEmail(REQUIRED_FIELD);
        } else if (!EMAIL_REGEX.test(value)) {
          setFormErrorOnEmail(true);
          setHelperTextOnEmail(EMAIL_EXCEPTION);
        } else {
          setHelperTextOnEmail(null);
          setFormErrorOnEmail(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFirstDisabled(false);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'username':
        setUsername(value);
        setFormErrorOnUsername(false);
        setHelperTextOnUsername(null);
        break;
      case 'email':
        setEmail(value);
        setFormErrorOnEmail(false);
        setHelperTextOnEmail(null);
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    handleRefreshUserData();

    setFormError(null);
    setFormErrorSeverity('error');
    setDisabled(false);
    setFirstDisabled(true);

    setUsername(userToBeUpdated.username || null);
    setUsernameClicked(false);
    setFormErrorOnUsername(false);
    setHelperTextOnUsername(null);

    setEmail(userToBeUpdated.email || null);
    setEmailClicked(false);
    setFormErrorOnEmail(false);
    setHelperTextOnEmail(null);
  };

  return (
    <Box sx={{ padding: { xs: 0, sm: 2 } }} component="form" autoComplete="off" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex' }}>
        <Typography variant="h6" sx={{ color: 'darkgray', ml: 2 }}>
          የፍቃድ መስክ አርትዕ
        </Typography>
      </Box>

      <Divider sx={{ mt: 1, borderStyle: 'dashed' }} />

      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          {formError && <Alert severity={formErrorSeverity}>{formError}</Alert>}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnUsername}
            id="username"
            name="username"
            label="የተጠቃሚ ስም"
            fullWidth
            // variant="standard"
            value={username}
            onChange={handleFormValueChange}
            helperText={formErrorOnUsername ? helperTextOnUsername : null}
            onFocus={() => setUsernameClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnEmail}
            id="email"
            name="email"
            placeholder="example@gmail.com"
            label="ኢሜይል"
            fullWidth
            // variant="standard"
            value={email}
            onChange={handleFormValueChange}
            helperText={formErrorOnEmail ? helperTextOnEmail : null}
            onFocus={() => setEmailClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', mt: 5 }}>
          <Button
            color="error"
            type="reset"
            onClick={handleClearForm}
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, md: 150 } }}
          >
            <RefreshIcon /> ዳግም አስጀምር
          </Button>{' '}
          {disabled && (
            <Button variant="contained" type="submit" sx={{ minWidth: { xs: 80, md: 150 } }} disabled>
              <CircularProgress style={{ width: '20px', height: '20px' }} />
              በማስቀመጥ ላይ...
            </Button>
          )}
          {!disabled && (
            <Button
              variant="contained"
              type="submit"
              sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, md: 150 } }}
              startIcon={<SaveTwoToneIcon />}
              disabled={disabled || firstDisabled}
            >
              ለውጦችን አስቀምጥ
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

EditCredential.propTypes = {
  userToBeUpdated: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  handleRefreshUserData: PropTypes.func.isRequired,
  updaterId: PropTypes.string.isRequired,
};
